import { useState, ReactNode } from 'react'
import { useResponsiveEnvironment } from '../../hooks/use-responsive-environment'
import { RangeEstimationDesktop } from './range-estimation-desktop'
import { RangeEstimationMobile } from './range-estimation-mobile'
import { rangeContent } from './range-estimation-content/range-estimation-content'
import styles from './range-estimation.module.css'

/**
 * Props for the controlled RangeEstimationModal component
 */
export interface RangeEstimationModalProps {
  isOpen: boolean
  rangeType?: string
  onClose: () => void
}

/**
 * Props for the uncontrolled RangeEstimationButtonAndModal component
 */
export interface RangeEstimationButtonAndModalProps {
  children: ReactNode
  rangeType?: string
}

/**
 * Controlled component for use when controlling the modal's open/close state externally.
 */
export const RangeEstimationModal = ({
  isOpen,
  rangeType = 'default',
  onClose,
}: RangeEstimationModalProps) => {
  const { isMobile } = useResponsiveEnvironment()
  const content = rangeContent[rangeType.toLowerCase()] ?? rangeContent.default

  if (!isOpen) {
    return null
  }

  /** Renders the appropriate variant based on the current viewport:
   * - Mobile: slide-up panel
   * - Desktop: modal dialog
   * Structure and behavior are similar, styling/CSS differs between variants.
   **/
  const ModalComponent = isMobile
    ? RangeEstimationMobile
    : RangeEstimationDesktop

  return <ModalComponent content={content} onClose={onClose} />
}

/**
 * Uncontrolled component that includes a button that manages its own modal state.
 */
export const RangeEstimationButtonAndModal = ({
  children,
  rangeType = 'default',
}: RangeEstimationButtonAndModalProps) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <>
      <button
        aria-label={'Range information'}
        className={styles.infoButton}
        type="button"
        onClick={(event) => {
          event.stopPropagation()
          setIsOpen(true)
        }}
      >
        {children}
      </button>
      <RangeEstimationModal
        isOpen={isOpen}
        rangeType={rangeType}
        onClose={() => setIsOpen(false)}
      />
    </>
  )
}
